import "./assets/loader.css";
import { initTheme } from "./shared/theme";

initTheme();
import "./assets/avc/theme.css";
import { BrowserRouter as Router, Routes, Route, useLocation } from "react-router-dom";
import NavBar from "./components/includes/NavBar";
import Home from "./pages/home/Home";
import Questions from "./pages/questions/Questions";
import Question from "./pages/questions/Question";
import Footer from "./components/includes/Footer";
import Blogs from "./pages/blogs/Blogs";
import Blog from "./pages/blogs/Blog";
import Category from "./pages/blogs/Category";
import BlogProvider from "./shared/context/BlogContext";
import QuestionProvider from "./shared/context/QuestionContext";
import Lawyers from "./pages/lawyers/Lawyers";
import LawyerProvider from "./shared/context/LawyerContext";
import Lawyer from "./pages/lawyers/Lawyer";
import AboutUs from "./pages/aboutus/AboutUs";
import ContactUs from "./pages/contactus/ContactUs";
import Terms from "./pages/terms/Terms";
import { urls } from "./shared/seo/links";
import React, { Suspense, lazy } from "react";
import UserProvider from "./shared/context/UserContext";
import jwtDecode from "jwt-decode";
import { useAuthContext } from "./shared/context/AuthContext";
import AppointmentProvider from "./shared/context/AppointementContext";
import EventProvider from "./shared/context/EventContext";
import ContactProvider from "./shared/context/ContactContext";
import NotFound from "./pages/NotFound/NotFound";
import ComponentsLoader from "./components/includes/ComponentsLoader";
import MobileHeader from "./components/includes/MobileHeader";
import FloatButton from "./components/includes/FloatButton";

// Pages crawlers never see (the console, accounts, dashboards, booking with
// its calendar and date picker, the CKEditor profile editor, the landing
// page) load on demand, so the public pages do not download them. While a
// chunk loads, <Suspense> shows the in-page loader (the boot splash is gone
// by then: index.js removes it after the first commit).
const OperationCenter = lazy(() => import(/* webpackChunkName: "operation-center" */ "./pages/operation_center/OperationCenter"));
const Login = lazy(() => import(/* webpackChunkName: "account" */ "./pages/login/Login"));
const Register = lazy(() => import(/* webpackChunkName: "account" */ "./pages/register/Register"));
const ResetPassword = lazy(() => import(/* webpackChunkName: "account" */ "./pages/login/ResetPassword"));
const UserVerification = lazy(() => import(/* webpackChunkName: "account" */ "./pages/user_verification/UserVerification"));
const Dashboard = lazy(() => import(/* webpackChunkName: "user-dashboard" */ "./pages/user_dashboard/Dashboard"));
const LawyerDashboard = lazy(() => import(/* webpackChunkName: "lawyer-dashboard" */ "./pages/lawyer_dashboard/LawyerDashboard"));
const BookAppointement = lazy(() => import(/* webpackChunkName: "booking" */ "./pages/book_appointement/BookAppointement"));
const ThankYou = lazy(() => import(/* webpackChunkName: "booking" */ "./pages/book_appointement/ThankYou"));
const LandingPage = lazy(() => import(/* webpackChunkName: "landing" */ "./pages/landing_page/LandingPage"));

function RouteLoader() {
  return (
    <div className="avc-route-loading">
      <ComponentsLoader />
    </div>
  );
}

// Each public section has one first path segment per language
// (avoconsulte-be/src/seo/urls.js). Routes match the percent-encoded path.
const segment = (type, lang) => "/" + encodeURI(urls.SECTIONS[type][lang]);
const perLanguage = (build) => urls.LANGS.map((lang) => build(lang));

// The Operation Center is its own console: no public header, footer or
// floating button around it.
function PublicChrome({ children }) {
  const { pathname } = useLocation();
  return pathname.startsWith("/operation-center") ? null : children;
}

function App() {
  // The mobile app identifies itself by user agent; it gets its own chrome.
  const isMobileView = navigator.userAgent === "AvoconsulteApp";
  const { Refresh } = useAuthContext();

  // The language (and with it the stylesheet, <html lang/dir> and i18n) is
  // decided from the URL by shared/seo/boot.js before the first render.
  let Authenticated = localStorage.getItem("user");
  let jwtAccessToken = localStorage.getItem("jwtAccessToken");
  let jwtRefreshToken = localStorage.getItem("jwtRefreshToken");

  async function getAccessToken(token) {
    let response = await Refresh(token);
    if (response.status === 200) {
      localStorage.setItem("jwtAccessToken", response.data.data.accessToken);
    } else {
      localStorage.removeItem("user");
      localStorage.removeItem("jwtAccessToken");
      localStorage.removeItem("jwtRefreshToken");
    }
  }

  if (Authenticated) {
    jwtDecode(jwtAccessToken).exp < Date.now() / 1000 &&
      getAccessToken(jwtRefreshToken);
  }

  const lawyersPage = (
    <LawyerProvider>
      <Lawyers />
    </LawyerProvider>
  );
  const lawyerPage = (
    <LawyerProvider>
      <Lawyer />
    </LawyerProvider>
  );
  const blogsPage = (
    <BlogProvider>
      <Blogs />
    </BlogProvider>
  );
  const categoryPage = (
    <BlogProvider>
      <Category />
    </BlogProvider>
  );
  const blogPage = (
    <BlogProvider>
      <Blog />
    </BlogProvider>
  );
  const questionsPage = (
    <QuestionProvider>
      <Questions />
    </QuestionProvider>
  );
  const questionPage = (
    <LawyerProvider>
      <QuestionProvider>
        <Question />
      </QuestionProvider>
    </LawyerProvider>
  );
  const contactPage = (
    <ContactProvider>
      <ContactUs />
    </ContactProvider>
  );

  return (
    <Router>
      <PublicChrome>{isMobileView ? <FloatButton /> : <NavBar />}</PublicChrome>
      <Suspense fallback={<RouteLoader />}>
      <Routes>
        <Route path="/operation-center/*" element={<OperationCenter />} />
        <Route path="/" element={<Home />} />
        <Route path="/fr" element={<Home />} />
        <Route path="/en" element={<Home />} />
        {perLanguage((lang) => (
          <React.Fragment key={lang}>
            <Route path={segment("lawyers", lang)} element={lawyersPage} />
            <Route path={segment("lawyers", lang) + "/:state"} element={lawyersPage} />
            <Route path={segment("lawyers", lang) + "/:state/:level"} element={lawyersPage} />
            <Route path={segment("lawyer", lang) + "/:name/:id"} element={lawyerPage} />
            <Route path={segment("blogs", lang)} element={blogsPage} />
            <Route path={segment("blogs", lang) + "/" + encodeURI(urls.CATEGORY_SEGMENT[lang]) + "/:category"} element={categoryPage} />
            <Route path={segment("blogs", lang) + "/:title/:id"} element={blogPage} />
            <Route path={segment("questions", lang)} element={questionsPage} />
            <Route path={segment("questions", lang) + "/:title/:id"} element={questionPage} />
            <Route path={segment("about", lang)} element={<AboutUs />} />
            <Route path={segment("contact", lang)} element={contactPage} />
            <Route path={segment("terms", lang)} element={<Terms />} />
          </React.Fragment>
        ))}
        {/* Old spellings that were linked or indexed. */}
        <Route path="/Privacy-Policy" element={<Terms />} />
        <Route path="/Qestions-Reponses" element={questionsPage} />
        <Route path="/Qestions-Reponses/:title/:id" element={questionPage} />
        <Route path={"/:username"} element={lawyerPage} />
        <Route
          path="/login"
          element={
            <>
              {isMobileView && <MobileHeader />}
              <Login isMobile={isMobileView} />
            </>
          }
        />
        <Route
          path="/Register"
          element={
            <>
              {isMobileView && <MobileHeader />}
              <Register />
            </>
          }
        />
        <Route
          path="/User-Dashboard/"
          element={
            <AppointmentProvider>
              <QuestionProvider>
                <UserProvider>
                  <Dashboard />
                </UserProvider>
              </QuestionProvider>
            </AppointmentProvider>
          }
        />
        <Route
          path="/Lawyer-Dashboard/"
          element={
            <EventProvider>
              <AppointmentProvider>
                <QuestionProvider>
                  <UserProvider>
                    <LawyerDashboard />
                  </UserProvider>
                </QuestionProvider>
              </AppointmentProvider>
            </EventProvider>
          }
        />
        <Route
          path="/Book-Appointment/:id"
          element={
            <AppointmentProvider>
              <LawyerProvider>
                <BookAppointement />
              </LawyerProvider>
            </AppointmentProvider>
          }
        />
        <Route path="/Thank-You/" element={<ThankYou />} />
        <Route path="/*" element={<NotFound />} />
        <Route
          path="/verify/:token"
          element={
            <UserProvider>
              <UserVerification />
            </UserProvider>
          }
        />
        <Route path="/reset-password/:token" element={<ResetPassword />} />
        <Route path="/Landing-Page" element={<LandingPage />} />
      </Routes>
      </Suspense>
      <PublicChrome>{isMobileView ? null : <Footer />}</PublicChrome>
    </Router>
  );
}

export default App;
